import { z } from 'zod';
import { Industry } from './controls.js';
/**
 * Client records (task 66, decision 0042). A client is a business an insurer works with, keyed by its
 * customer key like its assessments. The record holds what the insurer keeps about it besides the
 * assessments: the policy (number, premium, sum insured, renewal date, broker, line of business), where it
 * stands (prospect to bound, or lapsed), and the insurer's own custom fields. A client can be a prospect
 * with no assessment yet. Lapsed clients sit in the Archived tab.
 */
export const CLIENT_LIFECYCLE = ['prospect', 'assessing', 'quoted', 'bound', 'lapsed'];
export const LIFECYCLE_LABEL = {
    prospect: 'Prospect',
    assessing: 'Assessing',
    quoted: 'Quoted',
    bound: 'Bound',
    lapsed: 'Lapsed',
};
export const LIFECYCLE_TONE = {
    prospect: 'neutral',
    assessing: 'warn',
    quoted: 'warn',
    bound: 'ok',
    lapsed: 'bad',
};
/**
 * Where a client stands as shown: what its record says, except that a prospect with an assessment is
 * assessing; a client without a record is assessing, or a prospect while it has no assessment.
 */
export const effectiveLifecycle = (stored, hasAssessment) => stored && !(stored === 'prospect' && hasAssessment) ? stored : hasAssessment ? 'assessing' : 'prospect';
/** Suggestions for line of business; any short text is accepted. */
export const LINES_OF_BUSINESS = ['Cyber', 'Cyber and crime', 'Commercial package', 'Professional indemnity']; // prettier-ignore
export const CustomerKey = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9 _.&'()-]*$/, "Letters, digits, spaces and . _ - & ' ( )");
// —— custom fields ——
export const CLIENT_FIELD_TYPES = ['text', 'number', 'currency', 'date', 'choice', 'boolean'];
export const CLIENT_FIELD_TYPE_LABEL = {
    text: 'Text',
    number: 'Number',
    currency: 'Amount (RM)',
    date: 'Date',
    choice: 'One of a list',
    boolean: 'Yes or no',
};
export const MAX_CLIENT_FIELDS = 30;
const FieldKey = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'lower_snake_case, up to 40 characters');
const Options = z
    .array(z.string().trim().min(1).max(100))
    .max(30)
    .refine((o) => new Set(o).size === o.length, 'Options must be different');
export const ClientFieldInput = z
    .strictObject({
    key: FieldKey,
    label: z.string().trim().min(1).max(100),
    type: z.enum(CLIENT_FIELD_TYPES),
    options: Options.default([]),
    help: z.string().trim().max(300).default(''),
})
    .superRefine((f, ctx) => {
    if (f.type === 'choice' && !f.options.length)
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'A list needs its options' }); // prettier-ignore
    if (f.type !== 'choice' && f.options.length)
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'Only a list has options' }); // prettier-ignore
});
/** The key is fixed; the type is fixed once any client has a value; options can be added, not removed while used. */
export const ClientFieldPatch = z.strictObject({
    label: z.string().trim().min(1).max(100).optional(),
    type: z.enum(CLIENT_FIELD_TYPES).optional(),
    options: Options.optional(),
    help: z.string().trim().max(300).optional(),
});
const ISO = /^\d{4}-\d{2}-\d{2}$/;
/** A real calendar day as YYYY-MM-DD (Date.parse would take 30 February as 2 March). */
const realDate = (s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return ISO.test(s) && !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
/** A value for a custom field, as stored, or an error in words. Null and '' clear it. */
export function checkFieldValue(f, v) {
    // prettier-ignore
    if (v === null || v === undefined || v === '')
        return { ok: true, value: null };
    switch (f.type) {
        case 'text':
            return typeof v === 'string' && v.trim().length <= 500 ? { ok: true, value: v.trim() } : { ok: false, message: `${f.label}: up to 500 characters` }; // prettier-ignore
        case 'number':
        case 'currency': {
            const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(/[,\s]|^RM/gi, '')) : NaN;
            if (!Number.isFinite(n) || (f.type === 'currency' && n < 0))
                return { ok: false, message: `${f.label}: a number${f.type === 'currency' ? ' of ringgit, not negative' : ''}` }; // prettier-ignore
            return { ok: true, value: f.type === 'currency' ? Math.round(n * 100) / 100 : n };
        }
        case 'date':
            return typeof v === 'string' && realDate(v) ? { ok: true, value: v } : { ok: false, message: `${f.label}: a date as YYYY-MM-DD` }; // prettier-ignore
        case 'choice':
            return typeof v === 'string' && f.options.includes(v) ? { ok: true, value: v } : { ok: false, message: `${f.label}: one of ${f.options.join(', ')}` }; // prettier-ignore
        case 'boolean': {
            const b = typeof v === 'boolean' ? v : typeof v === 'string' ? { yes: true, true: true, ya: true, no: false, false: false, tidak: false }[v.trim().toLowerCase()] : undefined; // prettier-ignore
            return b === undefined ? { ok: false, message: `${f.label}: yes or no` } : { ok: true, value: b };
        }
    }
}
// —— the record ——
const Money = z.number().min(0).max(1e12);
const IsoDate = z.string().regex(ISO, 'YYYY-MM-DD').refine(realDate, 'not a real date');
const Text = (max) => z.string().trim().max(max);
/** What can be set on a record; null clears a field. Custom values are checked against the fields. */
export const ClientRecordFields = {
    customer_name: z.string().trim().min(1).max(200),
    industry: Industry.nullable(),
    country: z.string().length(2).toUpperCase().nullable(),
    policy_number: Text(60).nullable(),
    premium: Money.nullable(),
    sum_insured: Money.nullable(),
    renewal_date: IsoDate.nullable(),
    broker: Text(200).nullable(),
    line_of_business: Text(100).nullable(),
    lifecycle: z.enum(CLIENT_LIFECYCLE),
    /** The group's parent, for a subsidiary (task 94): another client with a record, itself not a subsidiary. */
    parent_key: CustomerKey.nullable(),
    custom: z.record(FieldKey, z.unknown()),
};
export const ClientRecordPatch = z
    .strictObject({ ...ClientRecordFields, revision: z.number().int().min(0) })
    .partial({ ...Object.fromEntries(Object.keys(ClientRecordFields).map((k) => [k, true])) }); // prettier-ignore
export const CreateClient = z
    .strictObject({ customer_key: CustomerKey, ...ClientRecordFields })
    .partial({ industry: true, country: true, policy_number: true, premium: true, sum_insured: true, renewal_date: true, broker: true, line_of_business: true, lifecycle: true, parent_key: true, custom: true }); // prettier-ignore
// —— saved views ——
export const SAVED_VIEW_LISTS = ['clients', 'assessments'];
export const CreateSavedView = z.strictObject({
    list: z.enum(SAVED_VIEW_LISTS),
    name: z.string().trim().min(1).max(60),
    /** The list's query string (filters, search, sort), without the page. */
    query: z
        .string()
        .max(1000)
        .transform((q) => q.replace(/^[?#]/, ''))
        .refine((q) => !/(^|&)page=/.test(q), 'Leave the page out'),
    /** Everyone in the organisation sees it, not only its author. */
    shared: z.boolean().default(false),
});
//# sourceMappingURL=clients.js.map